/**
 * HISTORY DRAWER — the recency-ordered lane-v2 session list (`GET /api/sessions` = R6).
 *
 * Pure so the sort/filter rule is unit-testable without rendering the drawer.
 */
import { taskStateLabel } from "./presentation";
import type { SessionListing } from "./types";

export type SessionRowSource = "v2";

export interface MergedSessionRow {
  id: string;
  source: SessionRowSource;
  title: string;
  /** Always null (the lane is routing detail, never shown). */
  lane: string | null;
  /** v2's `last_outcome` — an open enum, rendered verbatim when unknown. */
  status: string | null;
  /** ISO timestamp this row sorts by (most recent activity first). */
  activityAt: string;
  /** A second line under the title — only v2 rows have one today (task state / open questions). */
  subtitle: string | null;
  /** Legacy only — the run `openConversation` loads to open this row. */
  latestRunId: string | null;
  /** Legacy only — how many runs this session groups. */
  runCount: number | null;
  /** v2 only — null while still open. */
  closedAt: string | null;
  /** v2 only — 0 when none. */
  openAskCount: number | null;
}

function timeValue(iso: string | null | undefined): number {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : 0;
}

/** A task-less v2 session (no task yet) has no state to show. */
export function taskStateWords(state: string | null): string | null {
  if (!state) return null;
  return taskStateLabel(state, "history");
}

/** The line under a v2 row's title: task state, then open-question count, whichever apply. */
export function v2Subtitle(session: Pick<SessionListing, "tasks" | "open_ask_count">): string | null {
  const parts: string[] = [];
  const state = taskStateWords(session.tasks?.latest_state ?? null);
  if (state) parts.push(state);
  if (session.open_ask_count > 0) {
    parts.push(`${session.open_ask_count} ${session.open_ask_count === 1 ? "question" : "questions"} for you`);
  }
  return parts.length ? parts.join(" · ") : null;
}

export function v2Row(session: SessionListing): MergedSessionRow {
  return {
    id: session.session_id,
    source: "v2",
    title: session.title || "(no intent)",
    lane: null,
    status: session.last_outcome,
    activityAt: session.last_turn_at ?? session.created_at,
    subtitle: v2Subtitle(session),
    latestRunId: null,
    runCount: session.turn_count,
    closedAt: session.closed_at,
    openAskCount: session.open_ask_count,
  };
}

/**
 * The v2 sessions (R6) as one recency-ordered list. A closed session is dropped unless `showClosed`.
 */
export function sessionRows(v2: readonly SessionListing[], opts: { showClosed?: boolean } = {}): MergedSessionRow[] {
  const showClosed = opts.showClosed ?? false;
  return v2
    .filter((s) => showClosed || !s.closed_at)
    .map(v2Row)
    .sort((a, b) => timeValue(b.activityAt) - timeValue(a.activityAt));
}
